import { useState } from 'react'
import { Check, X, Ban, Clock } from 'lucide-react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import type { BookingStatus } from '@/lib/types/booking'
import { useBookingActions } from '@/lib/hooks/useBookingActions'
import { usePOSCurrency } from '@/lib/pos/currency'

interface Props {
  documentId: string
  status: BookingStatus
  metadata?: Record<string, unknown>
}

const MAIN_FLOW_STATUSES: BookingStatus[] = ['request', 'approved', 'arrived', 'left']

const TERMINAL_AFTER: Partial<Record<BookingStatus, number>> = {
  no_show:  2,
  canceled: 0,
  waitlist: 0,
  seated:   2,
}

function mainFlowIndex(status: BookingStatus) {
  return MAIN_FLOW_STATUSES.findIndex(s => s === status)
}

const isTerminal = (s: BookingStatus) => ['canceled', 'no_show', 'left'].includes(s)
const isOffPath  = (s: BookingStatus) => ['canceled', 'no_show', 'waitlist', 'seated'].includes(s)

export function BookingStatusTimeline({ documentId, status, metadata }: Props) {
  const { t } = useTranslation()
  const currency = usePOSCurrency()
  const bookingActions = useBookingActions()
  const [isPending, setIsPending] = useState(false)

  const [spend, setSpend] = useState<string>(String(metadata?.spend ?? ''))
  const [tip,   setTip]   = useState<string>(String(metadata?.tip   ?? '0'))

  const terminated = isTerminal(status)
  const offPath    = isOffPath(status)
  const progressIdx = offPath ? (TERMINAL_AFTER[status] ?? 0) : mainFlowIndex(status)

  const MAIN_FLOW: { status: BookingStatus; label: string }[] = [
    { status: 'request',  label: t('bookings.statusTimeline.requested') },
    { status: 'approved', label: t('bookings.statusTimeline.confirmed') },
    { status: 'arrived',  label: t('bookings.statusTimeline.arrived')   },
    { status: 'left',     label: t('bookings.statusTimeline.left')      },
  ]

  function mutate(targetStatus: BookingStatus) {
    setIsPending(true)
    bookingActions.updateStatus(documentId, targetStatus).finally(() => setIsPending(false))
  }

  function markLeft() {
    setIsPending(true)
    bookingActions.update(documentId, {
      status: 'left' as BookingStatus,
      metadata: {
        ...(metadata ?? {}),
        spend: spend === '' ? 0 : parseFloat(spend) || 0,
        tip:   tip   === '' ? 0 : parseFloat(tip)   || 0,
      },
    }).finally(() => setIsPending(false))
  }

  return (
    <div className="space-y-4">
      {/* Timeline strip */}
      <div className="flex items-start gap-0">
        {MAIN_FLOW.map((step, i) => {
          const done   = i < progressIdx
          const active = i === progressIdx && !offPath
          const future = i > progressIdx || (offPath && i >= progressIdx)

          return (
            <div key={step.status} className="flex items-start flex-1 min-w-0">
              <div className="flex flex-col items-center flex-1 min-w-0">
                <StepNode done={done} active={active} canceled={terminated && future} />
                <span
                  className={`mt-1.5 text-[10px] font-medium text-center leading-tight ${
                    active ? 'text-[var(--color-accent-amber)]' :
                    done   ? 'text-[var(--color-accent-green)]' :
                    'text-[var(--color-text-muted)]/50'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {i < MAIN_FLOW.length - 1 && (
                <div className="flex-1 h-[2px] mt-[11px] mx-[-1px]">
                  <div className={`h-full rounded-full transition-colors ${
                    i < progressIdx ? 'bg-[var(--color-accent-green)]' : 'bg-[var(--color-border-subtle)]'
                  }`} />
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Off-path terminal banners */}
      {status === 'canceled' && (
        <div className="flex items-center gap-2 rounded-[var(--radius-md)] bg-red-500/10 border border-red-500/20 px-3 py-2.5">
          <Ban className="h-4 w-4 text-red-400 shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-400">{t('bookings.statusTimeline.canceledTitle')}</p>
            <p className="text-xs text-red-400/70 mt-0.5">{t('bookings.statusTimeline.canceledDesc')}</p>
          </div>
          <button
            onClick={() => mutate('request')}
            disabled={isPending}
            className="ml-auto shrink-0 text-xs text-red-400 hover:text-red-300 underline underline-offset-2 disabled:opacity-40"
          >
            {t('bookings.statusTimeline.reopen')}
          </button>
        </div>
      )}

      {status === 'no_show' && (
        <div className="flex items-center gap-2 rounded-[var(--radius-md)] bg-orange-500/10 border border-orange-500/20 px-3 py-2.5">
          <X className="h-4 w-4 text-orange-400 shrink-0" />
          <div>
            <p className="text-sm font-medium text-orange-400">{t('bookings.statusTimeline.noShowTitle')}</p>
            <p className="text-xs text-orange-400/70 mt-0.5">{t('bookings.statusTimeline.noShowDesc')}</p>
          </div>
          <button
            onClick={() => mutate('arrived')}
            disabled={isPending}
            className="ml-auto shrink-0 text-xs text-orange-400 hover:text-orange-300 underline underline-offset-2 disabled:opacity-40"
          >
            {t('bookings.statusTimeline.undo')}
          </button>
        </div>
      )}

      {status === 'waitlist' && (
        <div className="flex items-center gap-2 rounded-[var(--radius-md)] bg-orange-500/10 border border-orange-500/20 px-3 py-2.5">
          <Clock className="h-4 w-4 text-orange-400 shrink-0" />
          <p className="text-sm font-medium text-orange-400">{t('bookings.statusTimeline.onWaitlist')}</p>
        </div>
      )}

      {/* Step actions */}
      {!terminated && (
        <>
          {/* Requested */}
          {status === 'request' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <ActionBtn label={t('bookings.statusTimeline.approve')}  onClick={() => mutate('approved')} isPending={isPending} variant="success" />
                <ActionBtn label={t('bookings.statusTimeline.decline')}  onClick={() => mutate('canceled')} isPending={isPending} variant="danger"  />
              </div>
              <CancelBtn onClick={() => mutate('canceled')} isPending={isPending} label={t('bookings.statusTimeline.cancelBooking')} />
            </>
          )}

          {/* Confirmed */}
          {(status === 'approved') && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <ActionBtn label={t('bookings.statusTimeline.markArrived')}  onClick={() => mutate('arrived')}  isPending={isPending} variant="primary" />
                <ActionBtn label={t('bookings.statusTimeline.noShow')}        onClick={() => mutate('no_show')}  isPending={isPending} variant="ghost-danger" />
              </div>
              <CancelBtn onClick={() => mutate('canceled')} isPending={isPending} label={t('bookings.statusTimeline.cancelBooking')} />
            </>
          )}

          {/* Arrived / Seated — spend + tip form */}
          {(status === 'arrived' || status === 'seated') && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-1">
                    {t('bookings.statusTimeline.amountSpent')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-[var(--color-text-muted)]">{currency}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={spend}
                      onChange={e => setSpend(e.target.value)}
                      placeholder="0"
                      className="w-full h-9 pl-12 pr-3 rounded-[var(--radius-md)] bg-[var(--color-bg-base)] border border-[var(--color-border-default)] text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-accent-amber)]/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-1">
                    {t('bookings.statusTimeline.tipLeft')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-[var(--color-text-muted)]">{currency}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={tip}
                      onChange={e => setTip(e.target.value)}
                      placeholder="0"
                      className="w-full h-9 pl-12 pr-3 rounded-[var(--radius-md)] bg-[var(--color-bg-base)] border border-[var(--color-border-default)] text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-accent-amber)]/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                </div>
              </div>
              <ActionBtn
                label={isPending ? t('bookings.statusTimeline.markingLeft') : t('bookings.statusTimeline.markLeft')}
                onClick={markLeft}
                isPending={isPending}
                variant="ghost"
                fullWidth
              />
            </div>
          )}

          {/* Waitlist */}
          {status === 'waitlist' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <ActionBtn label={t('bookings.statusTimeline.approve')} onClick={() => mutate('approved')} isPending={isPending} variant="success" />
                <ActionBtn label={t('common.cancel')}                   onClick={() => mutate('canceled')} isPending={isPending} variant="danger"  />
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

function StepNode({ done, active, canceled }: { done: boolean; active: boolean; canceled: boolean }) {
  return (
    <div className={`relative h-6 w-6 rounded-full flex items-center justify-center border-2 transition-all ${
      done    ? 'bg-[var(--color-accent-green)] border-[var(--color-accent-green)]' :
      active  ? 'bg-[var(--color-accent-amber)] border-[var(--color-accent-amber)]' :
      canceled ? 'bg-transparent border-[var(--color-border-subtle)] opacity-30' :
      'bg-transparent border-[var(--color-border-subtle)]'
    }`}>
      {done && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
      {active && (
        <motion.div
          className="h-2 w-2 rounded-full bg-white"
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
    </div>
  )
}

function CancelBtn({ onClick, isPending, label }: { onClick: () => void; isPending: boolean; label: string }) {
  return (
    <div className="pt-1 border-t border-[var(--color-border-subtle)]">
      <button
        onClick={onClick}
        disabled={isPending}
        className="w-full text-xs text-[var(--color-text-muted)] hover:text-red-400 transition-colors py-1.5 disabled:opacity-40"
      >
        {label}
      </button>
    </div>
  )
}

function ActionBtn({ label, onClick, isPending, variant, fullWidth }: {
  label: string
  onClick: () => void
  isPending: boolean
  variant: 'success' | 'primary' | 'danger' | 'ghost-danger' | 'ghost'
  fullWidth?: boolean
}) {
  const base = `${fullWidth ? 'w-full' : ''} h-9 px-4 rounded-[var(--radius-md)] text-sm font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`
  const styles: Record<string, string> = {
    success:        'bg-[var(--color-accent-green)] text-white hover:opacity-90',
    primary:        'bg-[var(--color-accent-amber)] text-white hover:opacity-90',
    danger:         'bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25',
    'ghost-danger': 'bg-transparent text-red-400/70 border border-red-500/15 hover:bg-red-500/10 hover:text-red-400',
    ghost:          'bg-[var(--color-bg-base)] border border-[var(--color-border-default)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]',
  }
  return (
    <button onClick={onClick} disabled={isPending} className={`${base} ${styles[variant]}`}>
      {label}
    </button>
  )
}
